import { AccountType } from '@/__generated__/graphql';
import type { Capability } from '@/core/capability';

import { Viewer } from './viewer';

/**
 * Check if a viewer has a capability.
 * Prefer `useCapability` hook in React components; use this for
 * non-React contexts (route actions, store logic).
 *
 * - With groupId: checks that specific group's capabilities
 * - Without groupId: checks user-level capabilities (aggregated by API permission engine)
 */
export function hasCapability(user: Viewer, capability: Capability, groupId?: string): boolean {
  if (user.admin) return true;
  if (groupId) {
    const group = (user.groups || []).find((g) => g?.id === groupId);
    return group?.capabilities?.[capability] === true;
  }
  return user.capabilities?.[capability] === true;
}

/** @deprecated Use hasCapability */
export function hasGroupCapability(user: Viewer, capability: Capability): boolean {
  return hasCapability(user, capability);
}

/** @deprecated Use `hasCapability(user, 'internalNetwork')` or useCapability hook */
export function isAdvancedUser(user: Viewer): boolean {
  return (
    user.admin ||
    (user.groups || []).some(
      (g) =>
        g?.account_type && [AccountType.Advanced, AccountType.Enterprise].includes(g.account_type)
    )
  );
}

/** @deprecated Use `hasCapability(user, 'internalNetwork')` or useCapability hook */
export function isEnterpriseUser(user: Viewer): boolean {
  return user.admin || (user.groups || []).some((g) => g?.account_type === AccountType.Enterprise);
}

/**
 * ENG-5251 (PR #2688 review S5): true when the viewer is a paying SMB
 * customer (any group with `billing_account.isSMBAccount === true`).
 *
 * Used to extend search-page eligibility — `isEnterpriseUser` is
 * AccountType-only and excludes the SMB tier, but SMB customers get
 * the `internalNetwork` capability per `BillingClassification.tla`
 * (CapabilityMatrix `internalNetwork |-> entSmb`), so the search page
 * gate `isEnterpriseUser(viewer) || hasSMBBilling(viewer)` correctly
 * matches what the API allows.
 *
 * The predicate shape mirrors the canonical SMB-customer signal at
 * `core/customer.ts:25` (`group?.billing_account?.isSMBAccount === true`),
 * which is governed by the `WebappSMBDerivation` invariant in
 * `api/tla/BillingClassification.tla`. No contract change required.
 *
 * Long-term, callers should migrate to
 * `hasCapability(user, 'internalNetwork')` — that path captures the same
 * eligibility via the capability matrix and is the non-deprecated API.
 * Doing so requires API-side capability wiring to flow through to viewer
 * payloads consistently for SMB users; out of scope for this PR.
 */
export function hasSMBBilling(user: Viewer): boolean {
  return (user.groups || []).some((g) => g?.billing_account?.isSMBAccount === true);
}
